import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';

const { FiMail, FiLock, FiEye, FiEyeOff, FiUser, FiUserCheck, FiInfo } = FiIcons;

const RegisterForm = ({ onToggleMode, onShowEmailConfirmation, onShowRegistrationSuccess }) => {
  const { signUp, inviteData, isValidatingInvite } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    full_name: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showInviteInfo, setShowInviteInfo] = useState(true);

  // Pre-fill form data when invite data is available
  useEffect(() => {
    if (inviteData) {
      console.log('Pre-filling registration form with invite data:', inviteData);
      
      setFormData(prevData => {
        const newData = { ...prevData };
        
        // Always set email from invite
        if (inviteData.email) {
          newData.email = inviteData.email;
        }
        
        // Set pre-filled data if available
        if (inviteData.pre_filled_data) {
          const preFilledData = inviteData.pre_filled_data;
          
          // Only override empty fields to allow user corrections
          if (preFilledData.first_name && !prevData.first_name) {
            newData.first_name = preFilledData.first_name;
          }
          if (preFilledData.last_name && !prevData.last_name) {
            newData.last_name = preFilledData.last_name;
          }
          if (preFilledData.full_name && !prevData.full_name) {
            newData.full_name = preFilledData.full_name;
          }
          
          // Log what was pre-filled for debugging
          console.log('Pre-filled form fields:', {
            firstName: preFilledData.first_name,
            lastName: preFilledData.last_name,
            fullName: preFilledData.full_name,
            role: preFilledData.role
          });
        }
        
        return newData;
      });
    } else {
      // Clear form if no invite data (unlikely but good practice)
      console.log('No invite data available for pre-population');
    }
  }, [inviteData]);

  // Auto-generate full name when first/last name changes
  useEffect(() => {
    if (formData.first_name || formData.last_name) {
      const generatedFullName = `${formData.first_name} ${formData.last_name}`.trim();
      if (generatedFullName !== formData.full_name) {
        setFormData(prev => ({
          ...prev,
          full_name: generatedFullName
        }));
      }
    }
  }, [formData.first_name, formData.last_name]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      // Prepare additional profile data
      const additionalData = {};
      
      if (formData.first_name) additionalData.first_name = formData.first_name;
      if (formData.last_name) additionalData.last_name = formData.last_name;
      if (formData.full_name) additionalData.full_name = formData.full_name;

      const { error } = await signUp(formData.email, formData.password, additionalData);
      if (error) throw error;
      
      // Show success page with email confirmation instructions
      if (onShowRegistrationSuccess) {
        onShowRegistrationSuccess(formData.email);
      } else if (onShowEmailConfirmation) {
        onShowEmailConfirmation(formData.email);
      } else if (onToggleMode) {
        onToggleMode();
      }
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'Super Admin';
      case 'COACH':
        return 'Coach';
      case 'CLIENT':
        return 'Client';
      default:
        return role;
    }
  };

  if (isValidatingInvite) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md mx-auto"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Validating Invite</h3>
            <p className="text-gray-600">Please wait while we validate your invitation...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md mx-auto"
    >
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {inviteData ? 'Complete Registration' : 'Create Account'}
          </h2>
          <p className="text-gray-600">
            {inviteData ? 'You\'ve been invited to join the platform.' : 'Join the AI Coach Assist platform.'}
          </p>
        </div>

        {/* Invite Information */}
        {inviteData && showInviteInfo && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <SafeIcon
                icon={FiInfo}
                className="text-blue-600 mt-0.5 mr-3 flex-shrink-0"
              />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-blue-900 mb-1">
                  Invitation Details
                </h4>
                <div className="text-sm text-blue-700 space-y-1">
                  <p>Email: <span className="font-medium">{inviteData.email}</span></p>
                  {inviteData.pre_filled_data?.role && (
                    <p>Role: <span className="font-medium">{getRoleDisplayName(inviteData.pre_filled_data.role)}</span></p>
                  )}
                  {inviteData.expires_at && (
                    <p>Expires: <span className="font-medium">
                      {new Date(inviteData.expires_at).toLocaleDateString()}
                    </span></p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowInviteInfo(false)}
                className="text-blue-400 hover:text-blue-600 ml-2"
              >
                <span className="sr-only">Dismiss</span>
                ×
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name Fields - Always show for invite users, and for non-invite registration */}
          {(!inviteData || inviteData) && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <div className="relative">
                  <SafeIcon
                    icon={FiUser}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="First name"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <div className="relative">
                  <SafeIcon
                    icon={FiUser}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Last name"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Full Name Field - Always show for invite users, and for non-invite registration */}
          {(!inviteData || inviteData) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <div className="relative">
                <SafeIcon
                  icon={FiUserCheck}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  placeholder="Full name (auto-generated)"
                  readOnly={!!(formData.first_name || formData.last_name)}
                />
              </div>
              {(formData.first_name || formData.last_name) && (
                <p className="mt-1 text-xs text-gray-500">Auto-generated from first and last name</p>
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <SafeIcon
                icon={FiMail}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={!!inviteData?.email}
                className={`w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  inviteData?.email ? 'bg-gray-50 cursor-not-allowed' : ''
                }`}
                placeholder="Enter your email"
              />
            </div>
            {inviteData?.email && (
              <p className="mt-1 text-xs text-gray-500">Email provided by invitation</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <SafeIcon 
                icon={FiLock} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Create a password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <SafeIcon 
                icon={FiLock} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type={showPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Confirm your password"
              />
            </div>
          </div>

          <motion.button
            type="submit"
            disabled={loading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Creating Account...' : inviteData ? 'Complete Registration' : 'Create Account'}
          </motion.button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Already have an account?{' '}
            <button
              onClick={onToggleMode}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default RegisterForm;